<script setup lang="ts">
import { ChevronRight } from 'lucide-vue-next'

import { isPreviewOnlySection, type BrandAppSection } from '../lib/brandAppSections'
import PreviewOnlyBadge from './PreviewOnlyBadge.vue'

const props = withDefaults(
  defineProps<{
    sections: BrandAppSection[]
    toggleIds?: string[]
    enabledIds?: string[]
    comingSoonIds?: string[]
  }>(),
  { toggleIds: () => [], enabledIds: () => [], comingSoonIds: () => [] }
)

const { t } = useI18n()

const hasToggle = (id: string) => props.toggleIds.includes(id)
const isEnabled = (id: string) => props.enabledIds.includes(id)
const isDisabled = (id: string) => hasToggle(id) && !isEnabled(id)
const isComingSoon = (id: string) => props.comingSoonIds.includes(id)
const isMuted = (id: string) => isDisabled(id) || isComingSoon(id)

const canDisable = computed(() => props.enabledIds.filter((id) => hasToggle(id)).length > 1)

const openSection = (id: string) => {
  if (!isComingSoon(id)) emit('open', id)
}

const emit = defineEmits<{
  open: [id: string]
  toggle: [id: string, enabled: boolean]
}>()
</script>

<template>
  <div class="flex flex-col gap-2 px-6">
    <div
      v-for="(section, index) in sections"
      :key="section.id"
      class="motion-safe:animate-card-in relative"
      :style="{ animationDelay: `${Math.min(index, 5) * 40}ms` }"
    >
      <Button
        variant="outline"
        class="hover:border-foreground-disabled h-auto w-full justify-start gap-3 p-3 text-left"
        :class="{ 'hover:border-border cursor-default opacity-60': isComingSoon(section.id) }"
        :aria-disabled="isComingSoon(section.id) || undefined"
        @click="openSection(section.id)"
      >
        <span class="bg-muted flex size-10 shrink-0 items-center justify-center rounded-xl">
          <component
            :is="section.icon"
            class="size-4.5"
            :class="isMuted(section.id) ? 'text-foreground-disabled' : 'text-primary'"
          />
        </span>
        <span
          class="min-w-0 truncate text-sm font-medium"
          :class="isMuted(section.id) ? 'text-foreground-secondary' : 'text-foreground'"
        >
          {{ section.name }}
        </span>
        <AppTooltip
          v-if="isComingSoon(section.id)"
          :text="t('pages.loyaltySupports.webapp.editor.screens.comingSoon')"
          class="ml-auto shrink-0"
        >
          <BaseChip :text="t('pages.loyaltySupports.webapp.editor.sections.comingSoon')" color="gray" />
        </AppTooltip>
        <PreviewOnlyBadge v-if="isPreviewOnlySection(section.id)" class="ml-auto" />
        <span
          v-if="hasToggle(section.id)"
          class="w-10 shrink-0"
          :class="{ 'ml-auto': !isPreviewOnlySection(section.id) }"
          aria-hidden="true"
        />
        <ChevronRight
          v-if="!isComingSoon(section.id)"
          class="text-foreground-disabled size-4 shrink-0"
          :class="{ 'ml-auto': !isPreviewOnlySection(section.id) && !hasToggle(section.id) }"
        />
      </Button>
      <Switch
        v-if="hasToggle(section.id)"
        :id="`section.${section.id}`"
        :model-value="isEnabled(section.id)"
        :disabled="isEnabled(section.id) && !canDisable"
        :aria-label="t('pages.loyaltySupports.webapp.editor.structure.orderedList.show', { name: section.name })"
        class="absolute top-1/2 right-10 -translate-y-1/2"
        @update:model-value="emit('toggle', section.id, $event)"
      />
    </div>
  </div>
</template>
